import { t } from "@lingui/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { useResponsiveProp } from "@shopify/restyle";
import graphql from "babel-plugin-relay/macro";
import { useState } from "react";
import { useFragment } from "react-relay";

import { FacilityLogo } from "@/features/Session/components/FacilityLogo";
import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import { HomeTabsNavigatorParamList } from "@/navigators/HomeTabsNavigator/types";
import { ScreenHeader } from "@whoosh/design-system/src/apps/PatronApp/layout/ScreenHeader";
import { Button } from "@whoosh/design-system/src/components/controls";
import { ArrowLeftIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { MobileFiltersBar } from "@whoosh/design-system/src/patterns/patronApp/MobileFiltersBar/MobileFiltersBar";

import { useGetDepartmentAvailabilityFilterBarValues } from "../hooks/useGetDepartmentAvailabilityFilterBarValues";
import { DepartmentAvailabilityFiltersModal } from "./DepartmentAvailabilityFiltersModal";
import { DepartmentAvailabilityHeaderFragment_department$key } from "./__generated__/DepartmentAvailabilityHeaderFragment_department.graphql";
import { DepartmentAvailabilityHeaderFragment_session$key } from "./__generated__/DepartmentAvailabilityHeaderFragment_session.graphql";

type Props = {
  departmentRef: DepartmentAvailabilityHeaderFragment_department$key | null;
  sessionRef: DepartmentAvailabilityHeaderFragment_session$key | null;
};
export const DepartmentAvailabilityHeader = ({
  departmentRef,
  sessionRef,
}: Props) => {
  const [isFilterModalOpen, setFilterModalOpen] = useState(false);
  const isMobile = useResponsiveProp({ base: 1, sm: 0 });

  const session = useFragment(
    DepartmentAvailabilityHeaderSessionFragmentString,
    sessionRef
  );

  const isSignedIn = !!session?.currentClientProfile?.member?.id;

  const department = useFragment(
    DepartmentAvailabilityHeaderFragmentString,
    departmentRef
  );
  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();
  const homeNavigation =
    useNavigation<NavigationProp<HomeTabsNavigatorParamList>>();

  const club = department?.club;
  const clubName = department?.club?.name;

  const filterBarValues = useGetDepartmentAvailabilityFilterBarValues(
    department ?? null
  );

  const handleBackPress = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      homeNavigation.navigate("MainHome");
    }
  };

  const onFilterBarPress = () => {
    setFilterModalOpen(true);
  };

  const onClose = () => {
    setFilterModalOpen(false);
  };

  return (
    <>
      <ScreenHeader
        title={clubName}
        subHeading={t`Book a Tee Time`}
        variant={isMobile ? "CENTERED" : "REGULAR"}
        titleRightElement={
          <FacilityLogo
            clubRef={club ?? null}
            imageProps={{
              style: { height: 56, width: 56, borderRadius: 24 },
            }}
          />
        }
        leftItem={
          isSignedIn ? (
            <Button
              accessibilityLabel={t`Back`}
              variant="secondary"
              leftIcon={<ArrowLeftIcon />}
              onPress={handleBackPress}
              testID="mobile-header-back-button"
            />
          ) : null
        }
        rightItem={
          isMobile ? (
            <FacilityLogo
              clubRef={club ?? null}
              imageProps={{
                style: { height: 56, width: 56, borderRadius: 24 },
              }}
            />
          ) : null
        }
        bottomComponent={
          isMobile ? (
            <MobileFiltersBar
              title={{ boldText: t`Find Times` }}
              values={filterBarValues}
              onPress={onFilterBarPress}
            />
          ) : null
        }
      />
      {isMobile && isFilterModalOpen ? (
        <DepartmentAvailabilityFiltersModal
          isOpen={isFilterModalOpen}
          onClose={onClose}
          departmentRef={department ?? null}
          sessionRef={session ?? null}
        />
      ) : null}
    </>
  );
};

const DepartmentAvailabilityHeaderFragmentString = graphql`
  fragment DepartmentAvailabilityHeaderFragment_department on Department {
    ...useGetDepartmentAvailabilityFilterBarValues_department
    ...DepartmentAvailabilityFiltersModal_department
    club {
      name
      ...FacilityLogoFragment_club
    }
  }
`;

const DepartmentAvailabilityHeaderSessionFragmentString = graphql`
  fragment DepartmentAvailabilityHeaderFragment_session on Session {
    ...DepartmentAvailabilityFiltersModal_session
    currentClientProfile {
      member {
        ... on Customer {
          id
        }
        ... on Member {
          id
        }
      }
    }
  }
`;
